import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { permissionState, pushSupported, subscribeToPush, unsubscribeFromPush } from "../../lib/webPush";
import {
  bootstrapMobilePush,
  getPushPermissionState,
  openNotificationSettings,
} from "../../lib/mobilePush";
import { useAuth } from "../../context/AuthContext";
import { Bell, BellOff, AlertTriangle, CheckCircle2, Smartphone, Settings } from "lucide-react";
import { toast } from "sonner";

const CUSTOMER_TOPICS = [
  { key: "order_placed", label: "Order placed", desc: "Confirmation when your order is successful" },
  { key: "order_status", label: "Order status", desc: "Shipped, out for delivery, delivered" },
  { key: "back_in_stock", label: "Back in stock", desc: "Wishlisted items returning to stock" },
  { key: "new_reel", label: "New reels", desc: "Drops from creators you follow" },
];

const SELLER_TOPICS = [
  { key: "seller_new_order", label: "New orders", desc: "When a customer places an order" },
  { key: "seller_refund", label: "Refund requests", desc: "Refund initiated on one of your orders" },
];

const ADMIN_TOPICS = [
  { key: "admin_new_order", label: "All new orders", desc: "Every order placed on the platform" },
  { key: "admin_new_seller", label: "New seller signup", desc: "Seller awaiting approval" },
  { key: "admin_refund", label: "Refund requests", desc: "Any refund requested by a customer" },
];

/**
 * Mobile-app push status card. Visible only inside the Capacitor shell
 * (web users never see it). Shows current OS permission state and gives
 * the user a one-tap path back to Settings if they previously denied.
 */
function MobilePushCard() {
  const [state, setState] = useState("unavailable");
  const [busy, setBusy] = useState(false);
  const [serverDevices, setServerDevices] = useState(null);  // {count, devices:[]}
  const [serverBusy, setServerBusy] = useState(false);

  // Read current OS permission state (no prompt). Tracked in an
  // alive-flag IIFE so a fast unmount doesn't try to setState on a dead tree.
  useEffect(() => {
    let alive = true;
    (async () => {
      const s = await getPushPermissionState();
      if (alive) setState(s);
    })();
    return () => { alive = false; };
  }, []);

  const refresh = async () => {
    setState(await getPushPermissionState());
  };

  if (state === "unavailable") return null;  // not a Capacitor build

  const onEnable = async () => {
    setBusy(true);
    try {
      // bootstrapMobilePush() is idempotent across the session — when state
      // is 'prompt' it triggers the OS dialog; when state is 'denied' it
      // logs and bails (user must use the Settings button below).
      await bootstrapMobilePush();
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const onOpenSettings = async () => {
    const ok = await openNotificationSettings();
    if (!ok) {
      toast.error("Could not open Settings automatically. Open your phone's Settings, find this app, and turn on Notifications.");
    }
    await refresh();   // Android returns here after the user leaves Settings
  };

  // Self-service diagnostic — asks the server what devices it has
  // stored for the logged-in user on this tenant. Used to definitively
  // answer "did the registration call actually reach the backend?"
  const checkServerDevices = async () => {
    setServerBusy(true);
    try {
      const { data } = await api.get("/devices/me");
      setServerDevices(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load registered devices");
    } finally {
      setServerBusy(false);
    }
  };

  const granted = state === "granted";
  const denied = state === "denied";

  return (
    <div
      className="bg-white border border-[hsl(var(--kb-border))] p-6"
      data-testid="mobile-push-card"
    >
      <div className="flex items-start gap-4">
        <div
          className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${
            granted ? "bg-[hsl(var(--kb-primary))] text-white" : "bg-[hsl(var(--kb-surface-alt))]"
          }`}
        >
          <Smartphone size={20} />
        </div>
        <div className="flex-1">
          <div className="font-display text-2xl" data-testid="mobile-push-state-label">
            Mobile app notifications {granted ? "are on" : "are off"}
          </div>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">
            Native push delivered through the installed app. Independent from the browser toggle below.
          </p>
          {denied && (
            <div className="mt-3 flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>
                Notifications were previously declined. The OS no longer shows the prompt — you&apos;ll need to re-enable them from system Settings.
              </span>
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            {!granted && !denied && (
              <button
                onClick={onEnable}
                disabled={busy}
                className="kb-btn-primary"
                data-testid="mobile-push-enable-btn"
              >
                {busy ? "Asking…" : "Enable notifications"}
              </button>
            )}
            {denied && (
              <button
                onClick={onOpenSettings}
                className="kb-btn-primary inline-flex items-center gap-2"
                data-testid="mobile-push-open-settings-btn"
              >
                <Settings size={16} /> Open Settings
              </button>
            )}
            {granted && (
              <span
                className="inline-flex items-center gap-1 text-sm text-green-700"
                data-testid="mobile-push-granted-badge"
              >
                <CheckCircle2 size={14} /> Permission granted — this device is registered.
              </span>
            )}
          </div>
          {granted && (
            <div className="mt-4 pt-4 border-t border-[hsl(var(--kb-border))]">
              <button
                onClick={checkServerDevices}
                disabled={serverBusy}
                className="text-xs text-[hsl(var(--kb-primary))] hover:underline"
                data-testid="mobile-push-check-server-btn"
              >
                {serverBusy ? "Checking…" : "Did the token actually reach the server? Check now."}
              </button>
              {serverDevices && (
                <div
                  className="mt-2 text-[10px] font-mono bg-[hsl(var(--kb-surface-alt))] p-3"
                  data-testid="mobile-push-server-devices"
                >
                  <div className="font-bold mb-1">
                    Server has {serverDevices.count} device(s) for you on this tenant.
                  </div>
                  {serverDevices.devices.map((d) => (
                    <div key={d.device_id} className="flex flex-wrap items-center gap-2 py-0.5">
                      <span className="bg-white border border-[hsl(var(--kb-border))] px-1 uppercase">{d.platform}</span>
                      <span>{d.token_suffix}</span>
                      <span className="text-[hsl(var(--kb-muted))]">{d.token_length}ch</span>
                      <span className="text-[hsl(var(--kb-muted))] truncate flex-1">{d.app_id || "—"}</span>
                      <span className="text-[hsl(var(--kb-muted))]">
                        last seen {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : "?"}
                      </span>
                    </div>
                  ))}
                  {serverDevices.count === 0 && (
                    <div className="text-red-700">
                      ✗ No devices stored. The OS gave us permission but the
                      token never reached the server. Check device logs
                      (Xcode console / adb logcat) for the watchdog warning
                      that says &ldquo;SILENT FAILURE&rdquo; — it lists the most
                      likely root cause.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function NotificationsTab() {
  const { user } = useAuth();
  const [perm, setPerm] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [prefsError, setPrefsError] = useState(null);

  const role = user?.role || "customer";
  const topicGroups = [
    { title: "Customer alerts", items: CUSTOMER_TOPICS },
    role === "seller" || role === "admin"
      ? { title: "Seller alerts", items: SELLER_TOPICS }
      : null,
    role === "admin" ? { title: "Admin alerts", items: ADMIN_TOPICS } : null,
  ].filter(Boolean);

  const refresh = async () => {
    setPerm(permissionState());
    try {
      if (pushSupported()) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(Boolean(sub));
      }
      const { data } = await api.get("/users/me/push-status");
      setPrefs(data.prefs);
      setPrefsError(null);
    } catch (e) {
      console.warn("notifications-load", e);
      setPrefsError(e?.response?.data?.detail || "Could not load notification preferences");
    }
  };

  useEffect(() => { refresh(); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      toast.success("Push notifications enabled");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Could not enable push");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      toast.success("Push notifications disabled");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to disable");
    } finally {
      setBusy(false);
    }
  };

  const togglePref = async (key, next) => {
    // Snapshot current state so rollback is race-safe (if two toggles fire
    // back-to-back, rolling back one by flipping `!next` would stomp the
    // other's optimistic value).
    const snapshot = prefs;
    setPrefs({ ...prefs, [key]: next });
    try {
      await api.put("/users/me/push-prefs", { [key]: next });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save preference");
      setPrefs(snapshot);
    }
  };

  if (!pushSupported()) {
    return (
      <div className="space-y-6 max-w-2xl" data-testid="notif-tab">
        <MobilePushCard />
        <div className="bg-white border border-[hsl(var(--kb-border))] p-6">
          <h2 className="font-display text-2xl mb-2">Push notifications</h2>
          <p className="text-sm text-[hsl(var(--kb-muted))]">
            Your browser doesn&apos;t support web push notifications. Try opening this site in Chrome, Firefox, Edge or Safari to enable them.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="notif-tab">
      <MobilePushCard />
      {/* Master toggle card */}
      <div className="bg-white border border-[hsl(var(--kb-border))] p-6">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${
            subscribed ? "bg-[hsl(var(--kb-primary))] text-white" : "bg-[hsl(var(--kb-surface-alt))]"
          }`}>
            {subscribed ? <Bell size={20} /> : <BellOff size={20} />}
          </div>
          <div className="flex-1">
            <div className="font-display text-2xl">
              Push notifications {subscribed ? "are on" : "are off"}
            </div>
            <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">
              Browser-native alerts — no SMS / WhatsApp involved. You can change topics below.
            </p>
            {perm === "denied" && (
              <div className="mt-3 flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>Notifications are blocked at the browser level. Click the lock icon in your address bar and allow notifications, then reload the page.</span>
              </div>
            )}
            <div className="mt-4">
              {!subscribed ? (
                <button
                  onClick={enable}
                  disabled={busy || perm === "denied"}
                  className="kb-btn-primary"
                  data-testid="notif-enable-btn"
                >
                  {busy ? "Enabling…" : "Enable notifications"}
                </button>
              ) : (
                <button
                  onClick={disable}
                  disabled={busy}
                  className="kb-btn-outline"
                  data-testid="notif-disable-btn"
                >
                  {busy ? "Disabling…" : "Disable on this device"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Per-topic toggles */}
      {prefsError ? (
        <div
          className="bg-white border border-red-200 text-red-700 px-4 py-3 text-sm flex items-start gap-2 max-w-2xl"
          data-testid="notif-prefs-error"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{prefsError}</span>
        </div>
      ) : !prefs ? (
        <div
          className="bg-white border border-[hsl(var(--kb-border))] p-6 max-w-2xl animate-pulse"
          data-testid="notif-prefs-loading"
        >
          <div className="h-4 w-32 bg-[hsl(var(--kb-surface-alt))] mb-4" />
          <div className="space-y-3">
            <div className="h-3 w-full bg-[hsl(var(--kb-surface-alt))]" />
            <div className="h-3 w-3/4 bg-[hsl(var(--kb-surface-alt))]" />
            <div className="h-3 w-2/3 bg-[hsl(var(--kb-surface-alt))]" />
          </div>
        </div>
      ) : topicGroups.map((g) => (
        <div key={g.title} className="bg-white border border-[hsl(var(--kb-border))] p-6">
          <h3 className="font-display text-lg mb-4">{g.title}</h3>
          <div className="space-y-3">
            {g.items.map((t) => (
              <label
                key={t.key}
                className="flex items-start gap-3 cursor-pointer"
                data-testid={`notif-topic-${t.key}`}
              >
                <input
                  type="checkbox"
                  className="mt-1 cursor-pointer"
                  checked={Boolean(prefs[t.key])}
                  onChange={(e) => togglePref(t.key, e.target.checked)}
                  data-testid={`notif-toggle-${t.key}`}
                />
                <div className="flex-1">
                  <div className="text-sm font-medium flex items-center gap-1">
                    {t.label}
                    {prefs[t.key] && <CheckCircle2 size={12} className="text-green-600" />}
                  </div>
                  <div className="text-xs text-[hsl(var(--kb-muted))]">{t.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
